/**
 * Format: Multi-store
 * -----------------------------------------------------------------------------
 * Formats Shopify storefront API multi-store responses.
 *
 */

/**
 * Format localization.
 * - https://shopify.dev/docs/api/storefront/2023-07/objects/Localization
 * @param {Object} countries - GraphQL response.
 * @param {Array} data.instances - Shopify instances.
 * @param {Array} data.theCountries - Countries to be prefixed with 'the'.
 * @param {Array} data.nonTranslatedLanguages - Languages that should render
 * in their language as well as the cnvs store language.
 * @returns {Object}
 */
export function formatCountries(
  countries,
  { instances, theCountries, nonTranslatedLanguages } = {},
) {
  const formattedCountries = {}

  /**
   * Find instance which has no countries assigned.
   */
  const unassignedInstance = instances.find((instance) => {
    return !instance.countries.length
  })

  /**
   * Format country response.
   */
  countries.localization.availableCountries.forEach((country) => {
    formattedCountries[country.code] = {
      country: {
        code: country.code.toUpperCase(),
        currency: country.currency?.code.toUpperCase(),
        currencySymbol: country.currency?.symbol,
        name: country.name,
        the: theCountries.includes(country.code),
      },
      instance: formatInstance(unassignedInstance),
      languages: country.availableLanguages.map((language) => {
        return {
          code: language.code.toUpperCase(),
          name: language.name,
        }
      }),
    }
  })

  /**
   * Add instance countries to list if they're not present in list.
   * - Ignores instances with no country codes set.
   */
  instances.forEach((instance) => {
    if (!instance.countries.length) {
      return
    }

    instance.countries.forEach((instanceCountry) => {
      const finalCountry = formattedCountries[instanceCountry.code] || {
        name: instanceCountry.name,
        code: instanceCountry.code,
        currency: instanceCountry.currency,
        currencySymbol: instanceCountry.currencySymbol,
        the: theCountries.includes(instanceCountry.code),
      }

      formattedCountries[instanceCountry.code] = {
        ...formattedCountries[instanceCountry.code],
        country: {
          ...finalCountry,
        },
        instance: formatInstance(instance),
        languages: instanceCountry.languages.map((languageCode) => {
          return {
            code: languageCode.toUpperCase(),
            name: formatLanguageOption(languageCode, nonTranslatedLanguages),
          }
        }),
      }
    })
  })

  return formattedCountries
}

/**
 * @param {String} languageCode - Target language code.
 * @param {Array} nonTranslatedLanguages - List of language codes to
 * render in its native language.
 */
export function formatLanguageOption(languageCode, nonTranslatedLanguages = []) {
  const defaultLanguageName = formatLanguageName().of(languageCode)

  return nonTranslatedLanguages.includes(languageCode.toUpperCase())
    ? `${defaultLanguageName}/${formatLanguageName(languageCode).of(languageCode)}`
    : defaultLanguageName
}

/**
   * Use Intl.DisplayNames API to translate language codes to their full,
   * translated name for instances.
   * @param {String} [languageCode] - Defaults to canvas store language, but
   * allows for languages to display in a specified language (i.e. 한국어 for
   * Korean).
   * @returns {String}
   */
function formatLanguageName(languageCode = [cnvs.store.language]) {
  return new Intl.DisplayNames(languageCode, { type: 'language' })
}

/**
 * Format TLD instance.
 * @param {Object} instance - Block setting instance object.
 * @returns {Object}
 */
function formatInstance(instance) {
  if (!instance) {
    return {
      url: false,
    }
  }

  /**
   * Ensure that URL ends with '/'.
   */
  const formattedMatchedUrl = instance.url.slice(-1) === '/'
    ? instance.url
    : `${instance.url}/`

  /**
   * Set URL to false if current store includes URL.
   * - URL is used to update /localization submit action so set to false if
   *   we want to use the default submit action.
   */
  let url = false
  const originWithoutWww = location.origin.replace('www.', '')

  if (
    !formattedMatchedUrl.includes('localhost') &&
    !formattedMatchedUrl.includes(location.origin) &&
    !formattedMatchedUrl.includes(originWithoutWww)
  ) {
    url = formattedMatchedUrl
  }

  /**
   * Return formatted object.
   */
  return {
    url,
  }
}
